import { ICommonSettings, ISlackSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigSlackSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: ISlackSetting;
}

export interface IConfigSlackSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingSlackId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigSlackSettings extends React.PureComponent<IConfigSlackSettingsProps, IConfigSlackSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigSlackSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigSlackSettingsProps & IWithSDProps) {
    if (prevProps.editingSlackId !== this.props.editingSlackId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingSlackId) return;

    const editingConfig = this.props.commonSettings.slackSettings?.find((x) => x.id === this.props.editingSlackId);

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const slackSetting = this.props.commonSettings?.slackSettings;
    if (!slackSetting) return;

    const isSettingAlreadyExist = slackSetting.some((x) => x.name === value && x.id !== this.props.editingSlackId);

    if (isSettingAlreadyExist) throw new Error("Slack Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ISlackSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      botToken: data.botToken,
      teamId: data.teamId
    };

    if (this.props.editingSlackId) {
      obj.id = this.props.editingSlackId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "slackSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "slackSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingSlackId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingSlackId || "",
      configKey: "slackSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Slack setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Slack setting"
        secondHeader="Slack setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingSlackId ? this.deleteSetting : undefined}
            deleteTestId="mcpSlackSettingDeleteButton"
            submitTestId="mcpSlackSettingCreateButton"
            submitLabel={this.props.editingSlackId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configSlackForm"
          />
        }
      >
        <Form
          id="configSlackForm"
          name="slackSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_slacksetting_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_slacksetting_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Slack Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Slack Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpSlackSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="BOT Token" required tooltipContentKey="mcp_slacksetting_bottoken">
              <Form.Item name="botToken" rules={[{ required: true, message: "Please enter a Slack BOT Token." }]}>
                <KuikaInput size="md" data-testid="mcpSlackSettingBotTokenInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Team Id" required tooltipContentKey="mcp_slacksetting_teamid">
              <Form.Item name="teamId" rules={[{ required: true, message: "Please enter a Slack Team Id." }]}>
                <KuikaInput size="md" data-testid="mcpSlackSettingTeamIdInput" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
